import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { BrandMark } from '@/components/ui';
import { AuthLayout } from './AuthLayout';
import { useAuthContext } from './useAuth';

export function FullScreenLoader() {
  return (
    <div className="grid h-dvh place-items-center bg-canvas" role="status" aria-label="Loading">
      <BrandMark className="size-16 animate-shimmer" />
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuthContext();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'unauthenticated') return <Navigate to="/signin" replace state={{ from: location }} />;
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { status, me } = useAuthContext();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'unauthenticated') return <Navigate to="/signin" replace state={{ from: location }} />;
  if (me?.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Layout route for sign in / sign up / etc. Signed in users bounce to where they were going. */
export function PublicOnlyLayout() {
  const { status } = useAuthContext();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'authenticated') {
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    return <Navigate to={from ? `${from.pathname}${from.search ?? ''}` : '/'} replace />;
  }
  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
}
